import { useState, useEffect, useLayoutEffect, useRef } from "react"
import { Link } from "react-router"

// The menu's gap from its button (mt-1 / mb-1), plus a little air from the
// edge of the screen.
const MENU_CLEARANCE_PX = 12

// Copy / Edit / Analytics actions for a code. On >= sm screens these render as
// three inline icon buttons; on a narrow (mobile) screen they collapse into a
// single overflow (kebab) menu so a list row doesn't run off the edge of the
// container. Owns its own "copied" feedback state.
export default function CodeActions({ code, onEdit }) {
  const [copied, setCopied] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [openUp, setOpenUp] = useState(false)
  const menuRef = useRef(null)
  const triggerRef = useRef(null)
  const listRef = useRef(null)

  function copyShortLink() {
    navigator.clipboard?.writeText(code.short_url || `tgo.sh/${code.token}`).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1400)
  }

  useEffect(() => {
    if (!menuOpen) return
    function onDoc(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
    function onKey(e) {
      if (e.key !== "Escape") return
      setMenuOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener("mousedown", onDoc)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDoc)
      document.removeEventListener("keydown", onKey)
    }
  }, [menuOpen])

  // The menu opens below its button. On a row near the bottom of the screen
  // there is no room there, so it opens above (#334). Measured before paint,
  // so it never shows in the wrong place first.
  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current || !listRef.current) return
    const button = triggerRef.current.getBoundingClientRect()
    const below = window.innerHeight - button.bottom
    const needed = listRef.current.offsetHeight + MENU_CLEARANCE_PX
    setOpenUp(below < needed && button.top > below)
  }, [menuOpen])

  const analyticsHref = `/app/codes/${code.token}`

  return (
    <>
      {/* Inline buttons — sm and up */}
      <div className="hidden sm:flex gap-1.5 flex-shrink-0">
        <IconBtn onClick={copyShortLink} label="Copy short link" active={copied}>
          {copied ? <CheckIcon /> : <CopyIcon />}
        </IconBtn>
        <IconBtn onClick={() => onEdit?.(code)} label="Edit code"><PencilIcon /></IconBtn>
        <Link
          to={analyticsHref}
          aria-label="View analytics"
          title="View analytics"
          className="w-[34px] h-[34px] inline-flex items-center justify-center border border-[color:var(--color-border)] dark:border-storm rounded text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud hover:border-voltage hover:bg-voltage-pale dark:hover:bg-voltage/15 transition"
        >
          <ChartIcon />
        </Link>
      </div>

      {/* Overflow menu — below sm */}
      <div className="sm:hidden relative flex-shrink-0" ref={menuRef}>
        <IconBtn ref={triggerRef} onClick={() => setMenuOpen((o) => !o)} label="Code actions" expanded={menuOpen}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
            <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
          </svg>
        </IconBtn>
        {menuOpen && (
          <div
            ref={listRef}
            role="menu"
            className={
              "absolute right-0 z-30 w-44 bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg shadow-lg overflow-hidden py-1 " +
              (openUp ? "bottom-full mb-1" : "top-full mt-1")
            }
          >
            <MenuItem onClick={() => { copyShortLink(); setMenuOpen(false) }}>
              <CopyIcon /> Copy short link
            </MenuItem>
            <MenuItem onClick={() => { setMenuOpen(false); onEdit?.(code) }}>
              <PencilIcon /> Edit code
            </MenuItem>
            <Link
              to={analyticsHref}
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-ink dark:text-cloud hover:bg-cloud dark:hover:bg-slate transition"
            >
              <ChartIcon /> Analytics
            </Link>
          </div>
        )}
      </div>
    </>
  )
}

function IconBtn({ ref, onClick, label, active = false, expanded, children }) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      className={
        "w-[34px] h-[34px] inline-flex items-center justify-center border rounded bg-transparent cursor-pointer transition " +
        (active
          ? "border-voltage text-voltage-dark dark:text-voltage-light bg-voltage-pale dark:bg-voltage/15"
          : "border-[color:var(--color-border)] dark:border-storm text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud hover:border-voltage hover:bg-voltage-pale dark:hover:bg-voltage/15")
      }
    >
      {children}
    </button>
  )
}

function MenuItem({ onClick, children }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-ink dark:text-cloud hover:bg-cloud dark:hover:bg-slate transition bg-transparent border-0 cursor-pointer text-left"
    >
      {children}
    </button>
  )
}

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
)
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-voltage-dark" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
)
const PencilIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
)
const ChartIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
)
